import React, { useState } from 'react';
import { Box, Typography, Card, FormControl, InputLabel, Select, MenuItem, Button, TextField, Grid, Chip } from '@mui/material';
import { PlayArrow, Compare as CompareIcon } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import SimulationProgress from './SimulationProgress';
import { accentColor, getTopologyColor } from './utils/topologyColors';
import { generateSolarProfile, generateEvLoadProfile, generateAcLoadProfile } from './utils/profileGenerators';
import { vortxTypography } from '@vortx/shared-ui';
import { runSimulation, runComparison } from '@vortx/shared-ui';

const SimulationConfig = ({ selectedTopologies = [], onSimulationComplete, onNavigateToResults }) => {
    const [durationMinutes, setDurationMinutes] = useState(1440);
    const [loadProfileType, setLoadProfileType] = useState('commercial');
    const [loading, setLoading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [status, setStatus] = useState('');
    const [error, setError] = useState(null);

    // Equipment configuration
    const [equipment, setEquipment] = useState({
        pv_kw: 100,
        bess_kw: 50,
        bess_kwh: 200,
        charger_count: 4,
        charger_kw: 50
    });

    // Determine if this is comparison mode
    const isComparisonMode = selectedTopologies.length === 2;
    const selectedTopology = selectedTopologies[0] || null;

    const handleEquipmentChange = (field, value) => {
        const numValue = value === '' ? 0 : parseFloat(value) || 0;
        setEquipment(prev => ({
            ...prev,
            [field]: numValue
        }));
    };

    // Format value for display - show empty string for 0 to prevent "05" issue
    const displayValue = (value) => value === 0 ? '' : value;

    const handleRunSimulation = async () => {
        if (selectedTopologies.length === 0) {
            setError('Please select a topology first');
            return;
        }

        setLoading(true);
        setProgress(0);
        setStatus(isComparisonMode ? 'Running comparison...' : 'Starting simulation...');
        setError(null);

        try {
            const solar_kw = generateSolarProfile(durationMinutes, equipment.pv_kw);
            const ev_load_kw = generateEvLoadProfile(durationMinutes, equipment.charger_count, equipment.charger_kw, loadProfileType);
            const ac_load_kw = generateAcLoadProfile(durationMinutes, 20);

            setStatus(isComparisonMode ? 'Comparing topologies...' : 'Submitting to solver...');
            setProgress(10);

            const profileConfig = {
                equipment,
                solar_kw,
                ev_load_kw,
                ac_load_kw,
                duration_minutes: durationMinutes,
            };

            if (isComparisonMode) {
                const data = await runComparison(
                    selectedTopologies.map(t => t.id),
                    profileConfig
                );

                setProgress(100);
                setStatus('Comparison complete');

                const simulationConfig = {
                    topology_ids: selectedTopologies.map(t => t.id),
                    equipment,
                    load_profile_type: loadProfileType,
                    duration_minutes: durationMinutes,
                };
                onSimulationComplete(data, simulationConfig, true);

            } else {
                const data = await runSimulation(selectedTopology.id, profileConfig);

                setProgress(100);
                setStatus('Complete');

                const simulationConfig = {
                    topology_id: selectedTopology.id,
                    equipment,
                    load_profile_type: loadProfileType,
                    duration_minutes: durationMinutes,
                };
                onSimulationComplete(data, simulationConfig, false);
            }

            setLoading(false);

            if (onNavigateToResults) {
                onNavigateToResults();
            }

        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    };

    return (
        <Box>
            <Typography sx={{
                fontFamily: vortxTypography.heading,
                fontSize: '1.5rem',
                fontWeight: 600,
                color: accentColor,
                mb: 1,
                textShadow: `0 0 20px ${alpha(accentColor, 0.5)}`,
            }}>
                {isComparisonMode ? 'Compare Topologies' : 'Run Simulation'}
            </Typography>
            <Typography sx={{
                fontFamily: vortxTypography.mono,
                fontSize: '0.8rem',
                color: '#888',
                mb: 3,
            }}>
                {isComparisonMode
                    ? 'Run parallel simulations to compare efficiency'
                    : 'Configure equipment and run minute-by-minute energy simulation'}
            </Typography>

            {/* Selected Topology/Topologies */}
            <Card sx={{
                bgcolor: 'rgba(30, 30, 30, 0.8)',
                border: `1px solid ${selectedTopologies.length > 0 ? accentColor : 'rgba(255,255,255,0.1)'}`,
                p: 2,
                mb: 3,
            }}>
                <Typography sx={{
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.65rem',
                    color: '#666',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    mb: 1,
                }}>
                    {isComparisonMode ? 'Comparing Topologies' : 'Selected Topology'}
                </Typography>

                {isComparisonMode ? (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {selectedTopologies.map(t => (
                            <Chip
                                key={t.id}
                                label={`#${t.id} ${t.name}`}
                                sx={{
                                    bgcolor: alpha(getTopologyColor(t.id), 0.2),
                                    color: getTopologyColor(t.id),
                                    fontFamily: vortxTypography.mono,
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                }}
                            />
                        ))}
                    </Box>
                ) : (
                    <>
                        <Typography sx={{
                            fontFamily: vortxTypography.heading,
                            fontSize: '1rem',
                            fontWeight: 600,
                            color: selectedTopology ? '#f4f1ec' : '#666',
                        }}>
                            {selectedTopology ? `#${selectedTopology.id} ${selectedTopology.name}` : 'None selected'}
                        </Typography>
                        {selectedTopology && (
                            <Box sx={{ mt: 1, display: 'flex', gap: 2 }}>
                                <Typography sx={{ fontFamily: vortxTypography.mono, fontSize: '0.7rem', color: '#888' }}>
                                    PV→EV: <span style={{ color: accentColor }}>{(selectedTopology.pv_to_ev_efficiency * 100).toFixed(1)}%</span>
                                </Typography>
                                <Typography sx={{ fontFamily: vortxTypography.mono, fontSize: '0.7rem', color: '#888' }}>
                                    Grid→EV: <span style={{ color: accentColor }}>{(selectedTopology.grid_to_ev_efficiency * 100).toFixed(1)}%</span>
                                </Typography>
                            </Box>
                        )}
                    </>
                )}

                {selectedTopologies.length === 0 && (
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.7rem',
                        color: '#e74c3c',
                        mt: 1,
                    }}>
                        Go to Topologies section to select one
                    </Typography>
                )}
            </Card>

            {/* Equipment Configuration */}
            <Card sx={{
                bgcolor: 'rgba(30, 30, 30, 0.8)',
                border: '1px solid rgba(255,255,255,0.1)',
                p: 2,
                mb: 3,
            }}>
                <Typography sx={{
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.65rem',
                    color: '#666',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    mb: 2,
                }}>
                    Equipment Configuration
                </Typography>

                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="Solar PV (kW)"
                            type="number"
                            value={displayValue(equipment.pv_kw)}
                            onChange={(e) => handleEquipmentChange('pv_kw', e.target.value)}
                            placeholder="0"
                            size="small"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: '#f4f1ec',
                                    '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                                    '&.Mui-focused fieldset': { borderColor: accentColor },
                                },
                                '& .MuiInputLabel-root': { color: '#888' },
                            }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="BESS Power (kW)"
                            type="number"
                            value={displayValue(equipment.bess_kw)}
                            onChange={(e) => handleEquipmentChange('bess_kw', e.target.value)}
                            placeholder="0"
                            size="small"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: '#f4f1ec',
                                    '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                                    '&.Mui-focused fieldset': { borderColor: accentColor },
                                },
                                '& .MuiInputLabel-root': { color: '#888' },
                            }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="BESS Capacity (kWh)"
                            type="number"
                            value={displayValue(equipment.bess_kwh)}
                            onChange={(e) => handleEquipmentChange('bess_kwh', e.target.value)}
                            placeholder="0"
                            size="small"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: '#f4f1ec',
                                    '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                                    '&.Mui-focused fieldset': { borderColor: accentColor },
                                },
                                '& .MuiInputLabel-root': { color: '#888' },
                            }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            label="Charger Count"
                            type="number"
                            value={displayValue(equipment.charger_count)}
                            onChange={(e) => handleEquipmentChange('charger_count', e.target.value)}
                            placeholder="0"
                            size="small"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: '#f4f1ec',
                                    '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                                    '&.Mui-focused fieldset': { borderColor: accentColor },
                                },
                                '& .MuiInputLabel-root': { color: '#888' },
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Charger Power (kW each)"
                            type="number"
                            value={displayValue(equipment.charger_kw)}
                            onChange={(e) => handleEquipmentChange('charger_kw', e.target.value)}
                            placeholder="0"
                            size="small"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: '#f4f1ec',
                                    '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                                    '&.Mui-focused fieldset': { borderColor: accentColor },
                                },
                                '& .MuiInputLabel-root': { color: '#888' },
                            }}
                        />
                    </Grid>
                </Grid>
            </Card>

            {/* Simulation Settings */}
            <Card sx={{
                bgcolor: 'rgba(30, 30, 30, 0.8)',
                border: '1px solid rgba(255,255,255,0.1)',
                p: 2,
                mb: 3,
            }}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel sx={{ color: '#888' }}>Duration</InputLabel>
                    <Select
                        value={durationMinutes}
                        label="Duration"
                        onChange={(e) => setDurationMinutes(e.target.value)}
                        sx={{
                            color: '#f4f1ec',
                            '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: accentColor },
                        }}
                    >
                        <MenuItem value={1440}>24 Hours (1,440 minutes)</MenuItem>
                        <MenuItem value={10080}>7 Days (10,080 minutes)</MenuItem>
                    </Select>
                </FormControl>

                <FormControl fullWidth>
                    <InputLabel sx={{ color: '#888' }}>Load Profile Type</InputLabel>
                    <Select
                        value={loadProfileType}
                        label="Load Profile Type"
                        onChange={(e) => setLoadProfileType(e.target.value)}
                        sx={{
                            color: '#f4f1ec',
                            '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: accentColor },
                        }}
                    >
                        <MenuItem value="commercial">Commercial (9-5 peak)</MenuItem>
                        <MenuItem value="fleet_depot">Fleet Depot (overnight charging)</MenuItem>
                        <MenuItem value="retail">Retail (daytime + evening)</MenuItem>
                        <MenuItem value="residential">Residential (morning + evening peaks)</MenuItem>
                    </Select>
                </FormControl>
            </Card>

            {/* Run Button */}
            <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={isComparisonMode ? <CompareIcon /> : <PlayArrow />}
                onClick={handleRunSimulation}
                disabled={loading || selectedTopologies.length === 0}
                sx={{
                    bgcolor: isComparisonMode ? '#2ecc71' : accentColor,
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.85rem',
                    py: 1.5,
                    '&:hover': {
                        bgcolor: isComparisonMode ? alpha('#2ecc71', 0.8) : alpha(accentColor, 0.8),
                        boxShadow: `0 0 30px ${alpha(isComparisonMode ? '#2ecc71' : accentColor, 0.5)}`,
                    },
                    '&:disabled': {
                        bgcolor: 'rgba(155, 89, 182, 0.3)',
                        color: 'rgba(255,255,255,0.3)',
                    },
                }}
            >
                {loading ? 'Running...' : (isComparisonMode ? 'Compare Topologies' : 'Run Simulation')}
            </Button>

            {/* Animated Simulation Progress */}
            <SimulationProgress
                isRunning={loading}
                progress={progress}
                status={status}
                equipment={equipment}
            />

            {/* Error */}
            {error && (
                <Typography sx={{
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.75rem',
                    color: '#e74c3c',
                    mt: 2,
                }}>
                    Error: {error}
                </Typography>
            )}
        </Box>
    );
};

export default SimulationConfig;
